import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, Shield, Search, Users, MapPin } from "lucide-react";
import api from "@/lib/api";
import { LOGO_URL } from "@/data/news";

const values = [
  { Icon: Search, title: "खोजी पत्रकारिता", text: "इन्वेस्टिगेटिव और ग्राउंड रिपोर्टिंग।" },
  { Icon: Shield, title: "तथ्य-सत्यापन", text: "हर खबर का संदर्भ और तथ्य जाँच।" },
  { Icon: Users, title: "जनसरोकार", text: "आम नागरिक के जीवन पर प्रभाव डालने वाले मुद्दे।" },
  { Icon: MapPin, title: "मध्यप्रदेश केंद्रित", text: "जिले-तहसील से जमीनी हकीकत।" },
];

export default function About() {
  const [leader, setLeader] = useState(null);

  useEffect(() => {
    api.get("/public/leadership").then((r) => setLeader(r.data)).catch(() => setLeader(null));
  }, []);

  return (
    <section id="about" data-testid="about-section" className="bg-[#EFEEE8] border-y border-[#0F0F0F]/20">
      {/* ---------- About Denva Post ---------- */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-20 md:py-24 grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-5">
          <div className="eyebrow text-[#D92525] mb-4">हमारे बारे में · About Denva Post</div>
          <h2 className="font-serif-dev font-black text-[38px] md:text-[54px] leading-[1.02]">
            खबरें नहीं, <span className="italic">भरोसा</span> छापते हैं।
          </h2>
          <p className="mt-6 font-serif-dev text-[17px] md:text-[18px] text-[#1a1a1a] leading-[1.85]">
            <b>Denva Post</b> जनवरी 2023 से पत्रकारिता में भरोसे का नाम बनने की दिशा में निरंतर कार्य कर रहा एक स्वतंत्र डिजिटल न्यूज़ प्लेटफ़ॉर्म है। हमारा उद्देश्य तथ्यों पर आधारित, निष्पक्ष और जनहित की पत्रकारिता को पाठकों तक पहुँचाना है।
          </p>
          <p className="mt-4 font-serif-dev text-[16px] text-[#1a1a1a] leading-[1.85]">
            हमारी पहचान <em className="not-italic font-semibold">इन्वेस्टिगेटिव जर्नलिज्म</em>, ग्राउंड रिपोर्टिंग, जनसरोकार, प्रशासनिक जवाबदेही, शिक्षा, स्वास्थ्य, पर्यावरण, अपराध, राजनीति और सामाजिक सरोकारों पर केंद्रित है।
          </p>
          <p className="mt-4 font-serif-dev text-[16px] text-[#1a1a1a] leading-[1.85]">
            वर्तमान में हमारी रिपोर्टिंग का प्रमुख फ़ोकस <b>मध्यप्रदेश</b> है। लक्ष्य—राज्य के हर जिले और तहसील से जमीनी हकीकत सामने लाना।
          </p>
          <p className="mt-5 font-serif-dev italic text-[15px] text-[#D92525] border-l-4 border-[#D92525] pl-4">
            Denva Post — तथ्य, पड़ताल और भरोसे की पत्रकारिता।
          </p>

          <a href="#leadership" data-testid="about-cta" className="mt-8 inline-flex pill-btn text-[12px]">
            नेतृत्व से मिलें <ArrowUpRight size={16} />
          </a>
        </div>

        <div className="col-span-12 lg:col-span-7">
          {/* Extra About content (replaces removed reader stats) */}
          <div className="bg-[#F4F4F0] border-l-4 border-[#D92525] p-8 md:p-10">
            <div className="eyebrow text-[#D92525] mb-3">हमारा तरीका · Our approach</div>
            <p className="font-serif-dev text-[17px] md:text-[18px] leading-[1.85] text-[#1a1a1a]">
              हमारा मानना है कि पत्रकारिता केवल घटनाओं की जानकारी देना नहीं, बल्कि उनके पीछे छिपे तथ्यों, कारणों और प्रभावों की गहराई तक जाकर उन्हें सामने लाना भी है।
            </p>
            <p className="mt-4 font-serif-dev text-[15px] md:text-[16px] leading-[1.85] text-[#4A4A4A]">
              Denva Post में प्रकाशित प्रत्येक समाचार, ग्राउंड रिपोर्ट और विश्लेषण <b className="text-[#1a1a1a]">संपादकीय मानकों, तथ्यात्मक सत्यापन और निष्पक्षता</b> के सिद्धांतों के आधार पर तैयार किया जाता है। हमारा प्रयास केवल खबर प्रकाशित करना नहीं, बल्कि पाठकों को विश्वसनीय, संदर्भपूर्ण और गहराई से समझ विकसित करने वाली पत्रकारिता उपलब्ध कराना है।
            </p>
            <p className="mt-4 font-serif-dev text-[15px] md:text-[16px] leading-[1.85] text-[#4A4A4A]">
              हम उन विषयों को प्राथमिकता देते हैं जिनका आम नागरिक के जीवन पर सीधा प्रभाव पड़ता है और जिन पर गंभीर, तथ्यपरक तथा संतुलित रिपोर्टिंग की आवश्यकता होती है।
            </p>
          </div>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {values.map(({ Icon, title, text }) => (
              <div key={title} className="bg-[#F4F4F0] p-5 border border-[#0F0F0F]/10 flex gap-3">
                <div className="w-10 h-10 shrink-0 rounded-full bg-[#0F0F0F] text-white flex items-center justify-center">
                  <Icon size={16} />
                </div>
                <div>
                  <div className="font-serif-dev font-bold text-[15px]">{title}</div>
                  <div className="text-[13px] text-[#4A4A4A] mt-0.5">{text}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 font-serif-dev italic text-[14px] text-[#4A4A4A]">
            "पत्रकारिता का पहला काम है—सच बोलना। बाकी सब उसके बाद आता है।" — संपादकीय दर्शन
          </p>
        </div>
      </div>

      {/* ---------- Leadership ---------- */}
      <div id="leadership" data-testid="leadership-section" className="border-t border-[#0F0F0F]/15 bg-[#F4F4F0]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-20">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-8 md:mb-10">
            <div>
              <div className="eyebrow text-[#D92525]">नेतृत्व · Leadership</div>
              <h3 className="mt-2 font-serif-dev font-black text-[30px] md:text-[42px] leading-[1.05]">
                जिनकी क़लम से आपकी खबरें
              </h3>
            </div>
            <div className="text-[13px] uppercase tracking-[0.18em] text-[#4A4A4A]">
              Editorial Board
            </div>
          </div>

          {leader ? (
            <Link
              to={`/author/${leader.id}`}
              data-testid="leadership-card-eic"
              className="group grid grid-cols-12 gap-6 md:gap-10 items-center bg-white border border-[#0F0F0F]/15 hover:border-[#D92525] transition-colors p-5 md:p-8 max-w-[900px]"
            >
              <div className="col-span-12 sm:col-span-4">
                <div className="aspect-[3/4] overflow-hidden bg-gray-100 border-4 border-[#0F0F0F]">
                  <img
                    src={leader.photo}
                    alt={leader.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    data-testid="leadership-photo"
                  />
                </div>
              </div>
              <div className="col-span-12 sm:col-span-8 min-w-0">
                <div className="eyebrow text-[#D92525] mb-2">{leader.designation || "Editor-in-Chief"}</div>
                <div
                  data-testid="leadership-name"
                  className="font-serif-dev font-black text-[26px] md:text-[34px] leading-tight text-[#0F0F0F] group-hover:text-[#D92525] transition-colors"
                >
                  {leader.name}
                </div>
                <p className="mt-3 font-serif-dev text-[15px] text-[#4A4A4A] leading-[1.75] line-clamp-4">
                  {leader.bio}
                </p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.18em] text-[#0F0F0F] group-hover:text-[#D92525]">
                  पूरी प्रोफ़ाइल देखें <ArrowUpRight size={14} />
                </div>
              </div>
            </Link>
          ) : (
            <div className="text-center py-10 text-[#4A4A4A]">…</div>
          )}
        </div>
      </div>

      {/* ---------- Join Us ---------- */}
      <div id="join" data-testid="join-section" className="border-t border-[#0F0F0F]/15 bg-[#0F0F0F] text-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-20 grid grid-cols-12 gap-10 items-center">
          <div className="col-span-12 md:col-span-7">
            <div className="eyebrow text-[#D92525] mb-3">हमारे साथ जुड़ें · Join us</div>
            <h3 className="font-serif-dev font-black text-[30px] md:text-[46px] leading-[1.05]">
              क़लम है? ज़मीन है? <span className="italic text-[#D92525]">आइए, साथ लिखें।</span>
            </h3>
            <p className="mt-5 font-serif-dev text-[15px] md:text-[17px] leading-[1.85] text-white/80">
              यदि आप भी इन्वेस्टिगेटिव स्टोरी, ग्राउंड रिपोर्ट, जनसरोकार और प्रभावशाली पत्रकारिता करने के इच्छुक हैं तथा <b className="text-white">Denva Post</b> के साथ रिपोर्टर, संवाददाता या स्ट्रिंगर के रूप में जुड़ना चाहते हैं, तो अपना संक्षिप्त परिचय, कार्यक्षेत्र (जिला/तहसील) और उपलब्ध कार्यों के नमूने हमें ईमेल करें।
            </p>
            <p className="mt-5 text-[13px] text-white/60">
              हम योग्य और प्रतिबद्ध पत्रकारों के साथ मिलकर मध्यप्रदेश के हर जिले और तहसील तक तथ्यपरक एवं जनहित की पत्रकारिता को मजबूत बनाने के लिए लगातार प्रयासरत हैं।
            </p>
          </div>

          <div className="col-span-12 md:col-span-5">
            <div className="bg-white text-[#0F0F0F] p-6 md:p-8 border-4 border-[#D92525]">
              <div className="eyebrow text-[#D92525] mb-3">Send your resume</div>
              <img src={LOGO_URL} alt="Denva Post" className="h-8 w-auto mb-4" />
              <div className="text-[12px] uppercase tracking-[0.18em] text-[#4A4A4A]">Email the Editor-in-Chief</div>
              <a
                href="mailto:EditorInChief@denvapost.com"
                data-testid="join-email-link"
                className="mt-2 flex items-center gap-2 font-serif-dev font-black text-[18px] md:text-[22px] text-[#0F0F0F] hover:text-[#D92525] break-all"
              >
                <Mail size={20} className="shrink-0" />
                EditorInChief@denvapost.com
              </a>
              <a
                href="mailto:EditorInChief@denvapost.com?subject=Denva%20Post%20%E0%A4%B0%E0%A4%BF%E0%A4%AA%E0%A5%8B%E0%A4%B0%E0%A5%8D%E0%A4%9F%E0%A4%B0%20%E0%A4%86%E0%A4%B5%E0%A5%87%E0%A4%A6%E0%A4%A8"
                data-testid="join-cta"
                className="mt-6 pill-btn text-[12px] w-full justify-center"
              >
                आवेदन भेजें <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
